import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../components/apiClient';
import PricingCard from '../../components/shared/PricingCard';

interface Feature {
  feature_id: string;
  feature_key: string;
  feature_name: string;
  feature_description: string;
  feature_type: 'quota' | 'boolean' | 'credits';
  reset_cycle: string;
  feature_limit: string | null;
  is_enabled: boolean;
}

interface Plan {
  id: string;
  plan_key: string;
  plan_name: string;
  plan_description: string;
  monthly_price_inr: string;
  academic_session_price_inr: string;
  is_free: boolean;
  is_default: boolean;
  plan_color: string;
  is_popular?: boolean;
  features: Feature[];
}

interface SubscriptionPlansResponse {
  status: string;
  result: Plan[];
  message: string;
}

const PricingTeaser: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/subscription/plans`);
        const data: SubscriptionPlansResponse = await response.json();
        
        if (data.status === 'success') {
          // Mark Premium plan as popular
          const updatedPlans = data.result.map(plan => ({
            ...plan,
            is_popular: plan.plan_key === 'premium'
          }));
          setPlans(updatedPlans);
        } else {
          setError('Failed to load plans');
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError('Failed to load plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  if (loading) {
    return (
      <div className="pricing-teaser py-5" style={{ background: '#ffffff' }}>
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || plans.length === 0) {
    return null; // Don't show pricing section if data can't be loaded
  }

  return (
    <div className="pricing-teaser py-5" style={{ background: '#ffffff' }}>
      <div className="container py-5">
        
        {/* Section Header */}
        <div className="text-center mb-5 pb-4">
          <div className="d-inline-flex align-items-center px-4 py-2 mb-3 rounded-pill"
            style={{
              background: 'linear-gradient(135deg, #667eea15, #764ba215)',
              border: '1px solid #667eea30'
            }}>
            <i className="fas fa-tags me-2 text-primary"></i>
            <span className="fw-semibold text-primary">Simple Pricing</span>
          </div>
          <h2 className="display-4 fw-bold mb-3">
            Choose Your <span style={{ 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Success Plan</span>
          </h2>
          <p className="lead text-muted mb-0" style={{ maxWidth: '700px', margin: '0 auto' }}>
            Start with our free plan and upgrade anytime. All plans include AI-powered features 
            to accelerate your JEE/NEET preparation.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="row g-4 justify-content-center">
          {plans.map((plan) => (
            <div key={plan.id} className="col-lg-3 col-md-6">
              <PricingCard
                plan={plan}
                billingCycle="monthly"
                showFeatures={true}
                variant="compact"
                ctaLink="/signup"
              />
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="row g-4 mt-5 pt-4">
          <div className="col-lg-3 col-md-6 text-center">
            <i className="fas fa-lock fa-2x text-primary mb-3"></i>
            <h6 className="fw-bold mb-2">Secure Payment</h6>
            <small className="text-muted">256-bit SSL encryption</small>
          </div>
          <div className="col-lg-3 col-md-6 text-center">
            <i className="fas fa-undo fa-2x text-primary mb-3"></i>
            <h6 className="fw-bold mb-2">Free Trial</h6>
            <small className="text-muted">1 Month Free access to major features</small>
          </div>
          <div className="col-lg-3 col-md-6 text-center">
            <i className="fas fa-headset fa-2x text-primary mb-3"></i>
            <h6 className="fw-bold mb-2">24/7 Support</h6>
            <small className="text-muted">Always here to help</small>
          </div>
          <div className="col-lg-3 col-md-6 text-center">
            <i className="fas fa-gift fa-2x text-primary mb-3"></i>
            <h6 className="fw-bold mb-2">Free Trial</h6>
            <small className="text-muted">Try before you buy</small>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-5 pt-4">
          <p className="text-muted mb-3">
            <i className="fas fa-info-circle me-2"></i>
            All plans include access to our AI-powered features. Upgrade or downgrade anytime.
          </p>
          <Link to="/pricing" className="btn btn-outline-primary px-4 py-2 rounded-pill">
            View Detailed Pricing <i className="fas fa-external-link-alt ms-2"></i>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PricingTeaser;
